import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar as CalendarIcon, ShieldCheck, Utensils } from 'lucide-react';
import { format, differenceInDays, addDays } from 'date-fns';

const MEAL_PLAN_OPTIONS = [
  { value: 'room_only', label: 'Room Only', perGuestPerDay: 0 },
  { value: 'breakfast', label: 'Breakfast (+₹150/guest/day)', perGuestPerDay: 150 },
  { value: 'breakfast_lunch', label: 'Breakfast + Lunch (+₹350/guest/day)', perGuestPerDay: 350 },
  { value: 'breakfast_dinner', label: 'Breakfast + Dinner (+₹400/guest/day)', perGuestPerDay: 400 },
  { value: 'all_meals', label: 'All Meals (+₹600/guest/day)', perGuestPerDay: 600 },
];

export const BookingFormDialog = ({
  open, onOpenChange, onSubmit, formData, setFormData,
  guests, rooms, checkInDate, setCheckInDate, checkOutDate, setCheckOutDate,
  editingBooking,
}) => {
  const { isAdmin } = useAuth();
  const isEdit = !!editingBooking;

  const selectedRoom = rooms.find(r => r.id === formData.room_id);
  const numGuests = parseInt(formData.num_guests) || 1;
  const mealPlan = formData.meal_plan || 'room_only';
  const customRate = parseFloat(formData.custom_rate) || 0;

  const pricePreview = useMemo(() => {
    if (!selectedRoom || !checkInDate || !checkOutDate) return null;
    const nights = Math.max(differenceInDays(checkOutDate, checkInDate), 1);
    const rate = customRate > 0 ? customRate : selectedRoom.price_per_night;
    const roomAmount = rate * nights;
    const mealRatePerDay = (MEAL_PLAN_OPTIONS.find(m => m.value === mealPlan)?.perGuestPerDay || 0) * numGuests;
    const mealAmount = mealRatePerDay * nights;
    const base = roomAmount + mealAmount;
    const gstRate = rate < 7500 ? 12 : 18;
    const gst = Math.round((base * gstRate) / 100 * 100) / 100;
    const total = Math.round((base + gst) * 100) / 100;
    return { nights, rate, roomAmount, mealRatePerDay, mealAmount, base, gstRate, gst, total };
  }, [selectedRoom, checkInDate, checkOutDate, customRate, mealPlan, numGuests]);

  // Smart date handlers:
  // 1. Changing check-in auto-bumps check-out to ci+1 if currently invalid (≤ new check-in)
  // 2. Calendar opens at the relevant month (not today's month)
  const handleCheckInChange = (date) => {
    if (!date) return;
    setCheckInDate(date);
    if (!checkOutDate || checkOutDate <= date) {
      setCheckOutDate(addDays(date, 1));
    }
  };

  const handleCheckOutChange = (date) => {
    if (!date) return;
    setCheckOutDate(date);
  };

  const today = new Date();
  const checkInMonth = checkInDate || today;
  const checkOutMonth = checkOutDate || (checkInDate ? addDays(checkInDate, 1) : today);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">{isEdit ? 'Edit Booking' : 'New Booking'}</DialogTitle>
          {isEdit && <p className="text-sm text-stone-500 mt-1">Booking: {editingBooking.booking_number}</p>}
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Guest</Label>
            <Select value={formData.guest_id} onValueChange={(value) => setFormData({...formData, guest_id: value})} disabled={isEdit}>
              <SelectTrigger className="border-stone-300" data-testid="select-guest">
                <SelectValue placeholder="Select a guest" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {guests.map(guest => (
                  <SelectItem key={guest.id} value={guest.id}>{guest.name} ({guest.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Room</Label>
            <Select value={formData.room_id} onValueChange={(value) => setFormData({...formData, room_id: value})}>
              <SelectTrigger className="border-stone-300" data-testid="select-room">
                <SelectValue placeholder="Select a room" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {rooms.map(room => (
                  <SelectItem key={room.id} value={room.id}>
                    Room {room.room_number} - {room.room_type} (₹{room.price_per_night}/night)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Check-in Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start border-stone-300 font-normal" data-testid="check-in-date-picker">
                    <CalendarIcon className="w-4 h-4 mr-2 text-stone-400" />
                    {checkInDate ? format(checkInDate, 'PP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar mode="single" selected={checkInDate} onSelect={handleCheckInChange} defaultMonth={checkInMonth} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Check-out Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start border-stone-300 font-normal" data-testid="check-out-date-picker">
                    <CalendarIcon className="w-4 h-4 mr-2 text-stone-400" />
                    {checkOutDate ? format(checkOutDate, 'PP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar mode="single" selected={checkOutDate} onSelect={handleCheckOutChange} defaultMonth={checkOutMonth} disabled={(date) => checkInDate && date <= checkInDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="num_guests">Number of Guests</Label>
              <Input id="num_guests" type="number" min="1" value={formData.num_guests} onChange={(e) => setFormData({...formData, num_guests: e.target.value})} className="border-stone-300" data-testid="num-guests-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="coupon_code">Coupon Code</Label>
              <Input id="coupon_code" value={formData.coupon_code || ''} onChange={(e) => setFormData({...formData, coupon_code: e.target.value.toUpperCase()})} placeholder="e.g. WELCOME20" className="border-stone-300 font-mono" data-testid="coupon-code-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Utensils className="w-3.5 h-3.5 text-stone-500" /> Meal Plan
            </Label>
            <Select value={mealPlan} onValueChange={(value) => setFormData({...formData, meal_plan: value})}>
              <SelectTrigger className="border-stone-300" data-testid="select-meal-plan">
                <SelectValue placeholder="Select meal plan" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {MEAL_PLAN_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`meal-plan-${opt.value}`}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isAdmin && (
            <div className="space-y-2">
              <Label htmlFor="custom_rate" className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-sage-700" />
                Custom Negotiated Rate <span className="text-xs text-stone-400 font-normal">(admin override, per night)</span>
              </Label>
              <Input
                id="custom_rate"
                type="number"
                min="0"
                step="1"
                value={formData.custom_rate ?? ''}
                onChange={(e) => setFormData({...formData, custom_rate: e.target.value})}
                placeholder={selectedRoom ? `Default ₹${selectedRoom.price_per_night}` : 'Leave blank for standard rate'}
                className="border-stone-300"
                data-testid="custom-rate-input"
              />
              <p className="text-xs text-stone-500">When set, this overrides the room rate and disables seasonal/occupancy multipliers.</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="special_requests">Special Requests</Label>
            <Textarea id="special_requests" value={formData.special_requests} onChange={(e) => setFormData({...formData, special_requests: e.target.value})} placeholder="Any special requests..." className="border-stone-300 min-h-[80px]" data-testid="special-requests-input" />
          </div>

          {pricePreview && (
            <div className="bg-stone-50 border border-stone-200 rounded-md p-3 text-sm space-y-1" data-testid="price-preview">
              <div className="flex justify-between text-stone-600">
                <span>Room ({pricePreview.nights} night{pricePreview.nights > 1 ? 's' : ''} × ₹{pricePreview.rate})</span>
                <span>₹{pricePreview.roomAmount.toFixed(2)}</span>
              </div>
              {pricePreview.mealAmount > 0 && (
                <div className="flex justify-between text-stone-600">
                  <span>Meals ({numGuests} guest{numGuests > 1 ? 's' : ''} × ₹{pricePreview.mealRatePerDay / numGuests}/day × {pricePreview.nights})</span>
                  <span>₹{pricePreview.mealAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-stone-600">
                <span>GST ({pricePreview.gstRate}%)</span>
                <span>₹{pricePreview.gst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-stone-900 font-semibold pt-1 border-t border-stone-200 mt-1">
                <span>Estimated Total</span>
                <span>₹{pricePreview.total.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-stone-400 italic">Coupon discount (if any) is applied server-side and may reduce the total.</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-stone-300">Cancel</Button>
            <Button type="submit" className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="create-booking-button">
              {isEdit ? 'Update Booking' : 'Create Booking'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
